<script setup lang="ts">
// TODO: migrate off deprecated package
import type { FlowStepperEmits, FlowStepperProps } from '@components/flow-controller'
import { IconArrowBackUp, IconArrowNarrowLeft, IconArrowNarrowRight } from '@tabler/icons-vue'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'

withDefaults(defineProps<FlowStepperProps>(), {
  showBackButton: true,
  showContinueButton: true,
  continueText: 'Continue',
  keepShoppingText: 'Keep shopping',
})

defineEmits<FlowStepperEmits>()
</script>

<template>
  <div class="flow-stepper" :class="{ 'keep-shopping-tray': showKeepShoppingButton }">
    <!-- No default slot — a gated inner span still registers as `$slots.default` and mounts an empty
         `label-container`, which throws off icon-only centering on desktop. -->
    <BaseButton
      v-if="showBackButton && showKeepShoppingButton"
      :aria-label="backText ?? 'Previous step'"
      class="back-btn icon-only"
      hierarchy="secondary"
      :iconBefore="IconArrowNarrowLeft"
      type="button"
      @click="$emit('back')"
    />

    <BaseButton
      v-else-if="showBackButton"
      class="back-btn"
      hideLabelOnMobile
      hierarchy="secondary"
      :iconBefore="IconArrowNarrowLeft"
      type="button"
      @click="$emit('back')"
    >
      <span v-if="backText" class="back-text">{{ backText }}</span>
    </BaseButton>

    <BaseButton
      v-if="showKeepShoppingButton"
      class="keep-shopping-btn"
      hierarchy="secondary"
      :iconAfter="IconArrowBackUp"
      :loading="isProcessing"
      type="button"
      @click="$emit('keepShopping')"
    >
      {{ keepShoppingText }}
    </BaseButton>

    <BaseButton
      v-if="showContinueButton"
      class="continue-btn"
      :iconAfter="IconArrowNarrowRight"
      :loading="isProcessing"
      type="submit"
    >
      {{ continueText }}
    </BaseButton>
  </div>
</template>

<style scoped>
.flow-stepper {
  --_btn-min-width-desktop: var(--touch-target-height);

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-0-75);

  .back-btn {
    flex: 0 0 auto;

    @media (--desktop-experience) {
      min-width: var(--_btn-min-width-desktop);
    }
  }

  .continue-btn {
    flex: 1;

    @media (--desktop-experience) {
      margin-left: auto;
      flex: 0 0 auto;
      min-width: var(--_btn-min-width-desktop);
    }
  }
}

.keep-shopping-tray {
  gap: var(--padding-cozy);
  padding-bottom: var(--padding-spacious);
  align-items: stretch;

  /* Stays icon-only above mobile too, unlike `hideLabelOnMobile` on its own (Figma 1416:24404). */
  .back-btn.icon-only {
    width: var(--touch-target-height);
    min-width: var(--touch-target-height);
    height: var(--touch-target-height);
    padding-inline: 0;
    flex: 0 0 var(--touch-target-height);
  }

  .keep-shopping-btn,
  .continue-btn {
    flex: 1 1 0;
    min-width: 0;
    margin-left: 0;
    height: var(--touch-target-height);
  }

  /* Keep shopping wraps below Back and Checkout, so visual order diverges from DOM and tab order
     (Figma 1416:24404). */
  @media (--mobile-experience) {
    flex-wrap: wrap;

    .back-btn.icon-only {
      order: 1;
    }

    .continue-btn {
      order: 2;
    }

    .keep-shopping-btn {
      order: 3;
      flex: 1 1 100%;
      width: 100%;
    }
  }
}
</style>
